// realm(탭/워커) 단위 인증 게이트.
//
// 목적: 토큰이 만료되면 프론트가 만료 토큰으로 요청을 폭주시켜 백엔드의 unauthorized rate
// limit 카운터를 터뜨리는 문제를 막는다. unauthorized 상태를 플래그로 캐시하고, refresh가
// 끝날 때까지 요청을 대기시켜 직렬화하며, 최종 실패 시엔 네트워크에 아예 보내지 않고
// 로컬에서 reject한다.
//
// 이 모듈은 Web Worker 번들과 SSR 번들 양쪽에 들어가므로 `window` / `document` /
// `localStorage`를 절대 참조하지 않는다. realm/탭 간 동기화는 BroadcastChannel로 한다.

const CHANNEL_NAME = 'viewup-auth-gate';
const DEFAULT_WAIT_TIMEOUT_MS = 10_000;

type GateMessage = { type: 'dead'; token: string } | { type: 'unauthorized' } | { type: 'reset' };

export class AuthGate {
  private deadTokens = new Set<string>();
  private unauthorized = false;
  private refreshPromise: Promise<string> | null = null;
  /**
   * 진행 중(또는 직전) refresh 시도가 **HTTP를 아예 보내지 못하고** 물러났는지(= 판단 보류).
   *
   * 디바운스나 락 실패로 시도조차 못 한 경우가 여기 해당한다. 이건 "인증이 만료됐다"는
   * 증거가 아니므로 최종 실패와 반드시 구분해야 한다 — 뭉뚱그리면 refresh를 한 번도
   * 보내보지 않은 채 게이트가 잠기고 모든 탭이 강제 로그아웃된다.
   *
   * **transport의 인스턴스 필드가 아니라 여기 사는 이유**: `refreshPromise` 슬롯은 정본
   * `AuthInterceptor`와 오토뷰잉 `HttpClient`가 **공유**한다. 한쪽 fn이 슬롯을 잡고 물러나면
   * 다른 쪽 인스턴스 필드는 건드려지지 않아 "보류였다"는 사실을 놓친다.
   *
   * 브로드캐스트하지 않는다 — 이 realm의 시도에 대한 서술이라 다른 탭엔 의미가 없다.
   */
  private deferred = false;
  private channel: BroadcastChannel | null = null;

  constructor() {
    if (typeof BroadcastChannel === 'undefined') return;

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      // Node(18+)의 BroadcastChannel은 전역이고 기본 ref 상태라, 이 모듈을 import하는
      // 것만으로 이벤트 루프를 붙잡는 핸들이 하나 생긴다 — `next build`의 페이지 수집
      // 워커나 CI 스크립트가 안 끝나고 늘어진다. 브라우저엔 `unref`가 없으므로 no-op.
      (this.channel as unknown as { unref?: () => void }).unref?.();
      this.channel.onmessage = (event: MessageEvent<GateMessage>) => {
        this.applyMessage(event.data);
      };
    } catch {
      // BroadcastChannel 생성 실패 — realm 내부 상태만으로 동작 (동기화 없이 동작)
      this.channel = null;
    }
  }

  isUnauthorized(): boolean {
    return this.unauthorized;
  }

  isDead(token: string): boolean {
    return this.deadTokens.has(token);
  }

  markDead(token: string): void {
    this.deadTokens.add(token);
    this.broadcast({ type: 'dead', token });
  }

  markUnauthorized(): void {
    this.unauthorized = true;
    this.broadcast({ type: 'unauthorized' });
  }

  reset(): void {
    this.deadTokens.clear();
    this.unauthorized = false;
    // 이 realm의 single-flight 슬롯도 완전히 비운다 — reset은 "이 게이트를 깨끗한 상태로
    // 되돌린다"는 의미이고, 여기서 지우지 않으면 실패한 채 멈춘 refresh(테스트의 예:
    // 영원히 resolve되지 않는 fn)가 다음 요청의 waitForRefresh를 계속 붙잡는다.
    // 원격 브로드캐스트 수신(`applyMessage`)에는 적용하지 않는다 — refreshPromise는
    // realm-local 개념이라 다른 realm의 reset이 이 realm의 진행 중인 refresh를
    // 건드릴 이유가 없다.
    this.refreshPromise = null;
    this.broadcast({ type: 'reset' });
  }

  // 진행 중인 refresh가 있으면 완료(성공/실패 무관)될 때까지 대기하고, 없으면 즉시 resolve된다.
  // 반드시 타임아웃을 둔다 — 다른 realm이 락을 물고 죽으면 앱 전체가 정지하기 때문이다.
  async waitForRefresh(timeoutMs: number = DEFAULT_WAIT_TIMEOUT_MS): Promise<void> {
    if (!this.refreshPromise) return;

    // Promise.race는 진 쪽을 취소하지 않는다. 타이머를 회수하지 않으면 게이트에서 대기한
    // 요청 수만큼(마운트 시 수십 건) 10초짜리 setTimeout이 클로저를 문 채 쌓인다.
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      await Promise.race([
        this.refreshPromise.catch(() => {}),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, timeoutMs);
        }),
      ]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  // single-flight: 이미 진행 중이면 같은 promise를 반환하고 fn을 다시 실행하지 않는다.
  runRefresh(fn: () => Promise<string>): Promise<string> {
    if (this.refreshPromise) return this.refreshPromise;

    this.deferred = false;
    this.refreshPromise = fn().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  // refresh HTTP를 아예 보내지 못하고 물러났음을 기록한다 (디바운스·락 실패).
  markRefreshDeferred(): void {
    this.deferred = true;
  }

  // 직전 refresh 시도가 보류였는가. 빈 토큰을 받았을 때 "최종 실패"로 단정하기 전에 확인한다.
  wasRefreshDeferred(): boolean {
    return this.deferred;
  }

  dispose(): void {
    this.channel?.close();
    this.channel = null;
  }

  private applyMessage(message: GateMessage): void {
    // BroadcastChannel 수신 핸들러는 상태만 반영한다 — 여기서 runRefresh를 트리거하면
    // 탭 수만큼 refresh stampede가 난다.
    switch (message.type) {
      case 'dead':
        this.deadTokens.add(message.token);
        return;
      case 'unauthorized':
        this.unauthorized = true;
        return;
      case 'reset':
        this.deadTokens.clear();
        this.unauthorized = false;
        return;
    }
  }

  private broadcast(message: GateMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch {
      // 브로드캐스트 실패는 무시 — 로컬 상태는 이미 반영된 뒤다.
    }
  }
}

export const authGate = new AuthGate();
